import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Building2, MapPin, Ruler, Calendar, FileText, ChevronRight, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';

const CustomerProjectForm = ({ open, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    project_name: '',
    location: '',
    plot_size: '',
    plot_dimensions: '',
    is_corner_plot: false,
    road_facing_width: '',
    floors_planned: 1,
    built_up_area: '',
    specific_requirements: '',
    has_approved_plan: false,
    soil_testing_done: false,
    planned_start_date: '',
    customer_notes: ''
  });

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        plot_size: parseFloat(formData.plot_size) || 0,
        built_up_area: parseFloat(formData.built_up_area) || 0,
        road_facing_width: formData.road_facing_width ? parseFloat(formData.road_facing_width) : null,
        floors_planned: parseInt(formData.floors_planned) || 1
      };
      
      await api.post('/projects/customer-submit', submitData);
      toast.success('Project request submitted successfully! Admin will review and contact you soon.');
      setFormData({
        project_name: '',
        location: '',
        plot_size: '',
        plot_dimensions: '',
        is_corner_plot: false,
        road_facing_width: '',
        floors_planned: 1,
        built_up_area: '',
        specific_requirements: '',
        has_approved_plan: false,
        soil_testing_done: false,
        planned_start_date: '',
        customer_notes: ''
      });
      setStep(1);
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit project request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setFormData({
      project_name: '',
      location: '',
      plot_size: '',
      plot_dimensions: '',
      is_corner_plot: false,
      road_facing_width: '',
      floors_planned: 1,
      built_up_area: '',
      specific_requirements: '',
      has_approved_plan: false,
      soil_testing_done: false,
      planned_start_date: '',
      customer_notes: ''
    });
    onClose();
  };

  const canProceedStep1 = formData.project_name && formData.location && formData.plot_size && formData.built_up_area;
  const canProceedStep2 = true; // Step 2 has optional fields

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg bg-white border-0 shadow-2xl rounded-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader className="pb-4 border-b border-stone-100">
          <DialogTitle className="text-xl font-semibold text-stone-900">
            Request New Project
          </DialogTitle>
          <DialogDescription className="text-stone-500">
            Step {step} of 2 - {step === 1 ? 'Plot Details' : 'Additional Information'}
          </DialogDescription>
        </DialogHeader>

        {/* Progress Indicator */}
        <div className="flex items-center space-x-2 py-2">
          <div className={`flex-1 h-1 rounded ${step >= 1 ? 'bg-[#1e3a5f]' : 'bg-stone-200'}`} />
          <div className={`flex-1 h-1 rounded ${step >= 2 ? 'bg-[#1e3a5f]' : 'bg-stone-200'}`} />
        </div>

        {step === 1 && (
          <div className="space-y-4 pt-2" data-testid="customer-project-form-step1">
            {/* Project Name */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-stone-700 flex items-center">
                <Building2 className="h-4 w-4 mr-2 text-stone-400" />
                Project Name *
              </Label>
              <Input
                value={formData.project_name}
                onChange={(e) => handleChange('project_name', e.target.value)}
                placeholder="e.g., My Dream Home"
                className="h-11 border-stone-200 rounded-lg"
                data-testid="project-name-input"
              />
            </div>

            {/* Location */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-stone-700 flex items-center">
                <MapPin className="h-4 w-4 mr-2 text-stone-400" />
                Location / Address *
              </Label>
              <Input
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder="Full address of the plot"
                className="h-11 border-stone-200 rounded-lg"
                data-testid="location-input"
              />
            </div>

            {/* Plot Size & Built-up Area */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-stone-700 flex items-center">
                  <Ruler className="h-4 w-4 mr-2 text-stone-400" />
                  Plot Size (sq.ft) *
                </Label>
                <Input
                  type="number"
                  value={formData.plot_size}
                  onChange={(e) => handleChange('plot_size', e.target.value)}
                  placeholder="e.g., 2400"
                  className="h-11 border-stone-200 rounded-lg"
                  data-testid="plot-size-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-stone-700">
                  Built-up Area (sq.ft) *
                </Label>
                <Input
                  type="number"
                  value={formData.built_up_area}
                  onChange={(e) => handleChange('built_up_area', e.target.value)}
                  placeholder="e.g., 3500"
                  className="h-11 border-stone-200 rounded-lg"
                  data-testid="built-up-area-input"
                />
              </div>
            </div>

            {/* Plot Dimensions */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-stone-700">
                Plot Dimensions (optional)
              </Label>
              <Input
                value={formData.plot_dimensions}
                onChange={(e) => handleChange('plot_dimensions', e.target.value)}
                placeholder="e.g., 40ft x 60ft"
                className="h-11 border-stone-200 rounded-lg"
              />
            </div>

            {/* Floors Planned */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-stone-700">
                Number of Floors Planned
              </Label>
              <Input
                type="number"
                min="1"
                max="10"
                value={formData.floors_planned}
                onChange={(e) => handleChange('floors_planned', e.target.value)}
                className="h-11 border-stone-200 rounded-lg"
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 pt-2" data-testid="customer-project-form-step2">
            {/* Corner Plot & Road Width */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-2 p-3 border border-stone-200 rounded-lg">
                <Checkbox
                  id="corner_plot"
                  checked={formData.is_corner_plot}
                  onCheckedChange={(checked) => handleChange('is_corner_plot', checked)}
                />
                <Label htmlFor="corner_plot" className="text-sm text-stone-700 cursor-pointer">
                  Corner Plot
                </Label>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-stone-700">
                  Road Facing Width (ft)
                </Label>
                <Input
                  type="number"
                  value={formData.road_facing_width}
                  onChange={(e) => handleChange('road_facing_width', e.target.value)}
                  placeholder="e.g., 30"
                  className="h-10 border-stone-200 rounded-lg"
                />
              </div>
            </div>

            {/* Approved Plan & Soil Testing */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-2 p-3 border border-stone-200 rounded-lg">
                <Checkbox
                  id="approved_plan"
                  checked={formData.has_approved_plan}
                  onCheckedChange={(checked) => handleChange('has_approved_plan', checked)}
                />
                <Label htmlFor="approved_plan" className="text-sm text-stone-700 cursor-pointer">
                  Have Approved Plan
                </Label>
              </div>
              <div className="flex items-center space-x-2 p-3 border border-stone-200 rounded-lg">
                <Checkbox
                  id="soil_testing"
                  checked={formData.soil_testing_done}
                  onCheckedChange={(checked) => handleChange('soil_testing_done', checked)}
                />
                <Label htmlFor="soil_testing" className="text-sm text-stone-700 cursor-pointer">
                  Soil Testing Done
                </Label>
              </div>
            </div>

            {/* Planned Start Date */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-stone-700 flex items-center">
                <Calendar className="h-4 w-4 mr-2 text-stone-400" />
                Preferred Start Date (optional)
              </Label>
              <Input
                type="date"
                value={formData.planned_start_date}
                onChange={(e) => handleChange('planned_start_date', e.target.value)}
                className="h-11 border-stone-200 rounded-lg"
              />
            </div>

            {/* Specific Requirements */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-stone-700 flex items-center">
                <FileText className="h-4 w-4 mr-2 text-stone-400" />
                Specific Requirements
              </Label>
              <Textarea
                value={formData.specific_requirements}
                onChange={(e) => handleChange('specific_requirements', e.target.value)}
                placeholder="Any specific requirements for your home (e.g., Vastu compliance, basement, terrace garden)"
                className="min-h-[80px] border-stone-200 rounded-lg"
              />
            </div>

            {/* Additional Notes */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-stone-700">
                Additional Notes
              </Label>
              <Textarea
                value={formData.customer_notes}
                onChange={(e) => handleChange('customer_notes', e.target.value)}
                placeholder="Any other information you'd like to share"
                className="min-h-[80px] border-stone-200 rounded-lg"
              />
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-3 pt-4 border-t border-stone-100">
          {step > 1 && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep(step - 1)}
              className="flex-1 h-11 border-stone-200 text-stone-700 hover:bg-stone-50 rounded-lg"
            >
              <ChevronLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          )}
          {step === 1 && (
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1 h-11 border-stone-200 text-stone-700 hover:bg-stone-50 rounded-lg"
            >
              Cancel
            </Button>
          )}
          {step < 2 ? (
            <Button
              type="button"
              onClick={() => setStep(step + 1)}
              disabled={!canProceedStep1}
              className="flex-1 h-11 bg-[#1e3a5f] hover:bg-[#2a4a6f] text-white rounded-lg"
              data-testid="next-step-button"
            >
              Next
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 h-11 bg-[#1e3a5f] hover:bg-[#2a4a6f] text-white rounded-lg"
              data-testid="submit-project-button"
            >
              {submitting ? (
                <span className="flex items-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Submitting...
                </span>
              ) : (
                'Submit Request'
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerProjectForm;
